// Agent 测试台:统一时间线模型。
// 所有对话事件(文本/工具调用/工具结果/流程事件)归一化为有序 TimelineItem,
// 渲染器按 kind 映射组件,新增事件类型只需加一个组件。

export type ToolLocation = 'agent' | 'frontend'
export type ToolCallStatus = 'running' | 'pending' | 'success' | 'error'

export interface TextItem {
  kind: 'user' | 'assistant'
  key: string
  content: string
  reasoning?: string
}

export interface ToolCallItem {
  kind: 'tool_call'
  key: string
  callId: string
  name: string
  args: unknown
  location: ToolLocation
  status: ToolCallStatus
  result?: string
  error?: string
  startedAt: number
  durationMs?: number
}

export interface EventItem {
  kind: 'event'
  key: string
  eventType: 'topic_changed' | 'flow_ended' | 'error'
  title: string
  detail?: string
}

export interface CompactionItem {
  kind: 'compaction'
  key: string
  headDropped: number
  summary: string
  trigger: 'auto' | 'manual'
  estTokensBefore?: number
  estTokensAfter?: number
}

export type TimelineItem = TextItem | ToolCallItem | EventItem | CompactionItem

let seq = 0
export function nextKey(prefix: string): string {
  seq += 1
  return `${prefix}_${Date.now().toString(36)}_${seq}`
}

export function parseArgs(raw: unknown): unknown {
  if (raw == null || raw === '') return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return raw
    }
  }
  return raw
}

export function prettyJson(v: unknown): string {
  if (typeof v === 'string') return v
  try {
    return JSON.stringify(v, null, 2)
  } catch {
    return String(v)
  }
}

/** 把 DB load 回来的 OpenAI 格式消息归一化为时间线条目。
 * assistant.tool_calls 展开为 tool_call 卡片;tool 消息按 tool_call_id 回填结果。
 * doc 123:metadata.compaction + metadata.summary 配对 → CompactionItem(折叠条)。
 */
export function normalizeTimeline(messages: any[]): TimelineItem[] {
  const items: TimelineItem[] = []
  const byCallId = new Map<string, ToolCallItem>()

  for (let i = 0; i < (messages || []).length; i++) {
    const m = messages[i]
    const role = m?.role
    const meta = m?.metadata ?? {}

    // doc 123:压缩对 — user(marker) + 紧跟 assistant(summary) 合并为折叠条
    if (role === 'user' && meta.compaction && i + 1 < messages.length) {
      const next = messages[i + 1]
      const nextMeta = next?.metadata ?? {}
      if (next?.role === 'assistant' && nextMeta.summary) {
        items.push({
          kind: 'compaction',
          key: nextKey('compaction'),
          headDropped: meta.head_dropped ?? 0,
          summary: next?.content ?? '',
          trigger: (meta.trigger as 'auto' | 'manual') ?? 'auto',
          estTokensBefore: meta.est_tokens_before,
          estTokensAfter: undefined,
        })
        i += 1 // 跳过配对的 summary
        continue
      }
    }

    if (role === 'user') {
      items.push({ kind: 'user', key: nextKey('u'), content: m.content ?? '' })
    } else if (role === 'assistant') {
      if (meta.summary) {
        // 已配对的跳过(由配对 user 触发处理);防御式独立压缩摘要
        continue
      }
      if (m.content || m.reasoning) {
        items.push({
          kind: 'assistant',
          key: nextKey('a'),
          content: m.content ?? '',
          reasoning: m.reasoning,
        })
      }
      if (Array.isArray(m.tool_calls)) {
        for (const tc of m.tool_calls) {
          const callId = tc?.id ?? nextKey('tc')
          const item: ToolCallItem = {
            kind: 'tool_call',
            key: nextKey('call'),
            callId,
            name: tc?.function?.name ?? 'unknown',
            args: parseArgs(tc?.function?.arguments),
            // 历史未记录执行位置,默认为 agent(frontend 工具当时由前端执行,不留服务端记录)
            location: 'agent',
            status: 'success',
            startedAt: 0,
          }
          items.push(item)
          byCallId.set(callId, item)
        }
      }
    } else if (role === 'tool') {
      const callId = m.tool_call_id ?? ''
      const target = byCallId.get(callId)
      if (target) {
        target.result = m.content ?? ''
      } else {
        // 无对应 call(历史不完整)→ 独立卡片兜底
        items.push({
          kind: 'tool_call',
          key: nextKey('call'),
          callId,
          name: 'tool',
          args: {},
          location: 'agent',
          status: 'success',
          result: m.content ?? '',
          startedAt: 0,
        })
      }
    }
  }
  return items
}